/**
 * CashierBottomNav — fixed-bottom mobile navigation for the cashier role.
 *
 * Block C0 (Jun 20): renders the PRIMARY tabs from the surface registry
 * (getSurfaces), so they flex per industry and auto-extend when surfaces go
 * live.
 *
 * Block C1 (Jun 21): "More" is now a REAL overflow menu, not a dead link.
 *   - Primary tabs = first MAX_PRIMARY route-surfaces by order.
 *   - Overflow = every remaining route-surface (schedule, expenditure, chat,
 *     account, …). Tapping "More" opens a pop-up sheet listing them, so a
 *     salon mobile cashier can reach "Appointments" (which sorts past the cap).
 *   This closes the desktop/mobile asymmetry: the desktop sidebar shows ALL
 *   surfaces; the bottom nav now reaches all of them too, from the same
 *   registry. No hardcoded MORE_BUCKET — overflow is derived.
 *
 * Mobile-only (<lg), cashier-role-only, safe-area padding, active green pill.
 * Sheet tools (Calculator, Saved carts) are intentionally NOT here — POS-local.
 */
import React, { useState } from "react";
import { usePermissions } from "@/hooks/usePermissions";
import { useEffectiveRole } from "@/components/auth/RouteGuards";
import { Link, useLocation } from "react-router-dom";
import { MoreHorizontal, X } from "lucide-react";
import { cn } from "@/lib/utils";
import useAuthStore, { isCashier } from "@/stores/authStore";
import { useT } from "@/hooks/useIndustry";
import { useChatUnread } from "@/hooks/useChatUnread";
import {
  getSurfaces,
  type SurfaceDef,
  type Role,
} from "@/constants/industry-surfaces";

const MAX_PRIMARY = 3;

function isSurfaceActive(currentPath: string, path: string): boolean {
  // /cashier is the POS home — exact match, not a section prefix.
  if (path === "/cashier") return currentPath === "/cashier";
  return currentPath === path || currentPath.startsWith(path + "/");
}

export const CashierBottomNav: React.FC = () => {
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const { t, industry } = useT();
  const chatUnread = useChatUnread();
  const [moreOpen, setMoreOpen] = useState(false);
  const { can } = usePermissions();
  const role = useEffectiveRole();

  // Only render for cashier role. Admins keep their desktop sidebar.
  // Cashier-level here (their level where they're working, not their best
  // role anywhere). Others reach the till from the sidebar.
  if (!user || !role || !isCashier(role)) return null;

  // All live cashier route-surfaces, ordered. Primary = first MAX_PRIMARY;
  // the rest spill into the "More" overflow sheet.
  const surfaces = getSurfaces({
    industry: industry.id,
    app: "cashier",
    role: "cashier" as Role,
    includePlanned: false,
    includeSheets: false,
  }).filter((s) => !s.perm || can(s.perm));
  const primary = surfaces.slice(0, MAX_PRIMARY);
  const overflow = surfaces.slice(MAX_PRIMARY);

  const labelFor = (s: SurfaceDef): string => {
    // The schedule surface's label is a NESTED term (terms.schedule.title),
    // which t(termKey) can't reach — resolve it directly so the same surface
    // reads "Appointments" / "Reservations" / "Deliveries" per industry.
    if (s.key === "core.schedule") {
      return industry.terms.schedule?.title ?? s.label;
    }
    return s.termKey ? t(s.termKey) : s.label;
  };

  const badgeFor = (s: SurfaceDef): number =>
    s.badgeKey === "chatUnread" ? chatUnread : 0;

  const overflowActive = overflow.some((s) =>
    isSurfaceActive(location.pathname, s.path!),
  );
  const overflowUnread = overflow.reduce((sum, s) => sum + badgeFor(s), 0);

  const closeMore = () => setMoreOpen(false);

  return (
    <>
      {/* Overflow sheet — pops above the bar; backdrop closes it. */}
      {moreOpen && overflow.length > 0 && (
        <>
          <button
            type="button"
            aria-label="Close menu"
            onClick={closeMore}
            className="lg:hidden fixed inset-0 z-40 bg-black/40"
          />
          <div
            className={cn(
              "lg:hidden fixed inset-x-0 z-50",
              "bottom-[calc(56px+env(safe-area-inset-bottom,0px))]",
              "bg-sidebar text-sidebar-foreground border-t border-sidebar-border",
              "rounded-t-2xl shadow-2xl",
            )}
            role="menu"
            aria-label="More cashier surfaces"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-sidebar-border">
              <span className="text-sm font-semibold text-white">More</span>
              <button
                type="button"
                onClick={closeMore}
                aria-label="Close"
                className="rounded-md p-1 text-sidebar-foreground/70 hover:bg-white/5 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>
            <ul className="py-1">
              {overflow.map((s) => {
                const Icon = s.icon;
                const active = isSurfaceActive(location.pathname, s.path!);
                const badge = badgeFor(s);
                return (
                  <li key={s.key}>
                    <Link
                      to={s.path!}
                      onClick={closeMore}
                      role="menuitem"
                      className={cn(
                        "flex items-center gap-3 px-4 py-3 transition-colors",
                        active
                          ? "text-white bg-white/5"
                          : "text-sidebar-foreground/80 hover:bg-white/5 hover:text-white",
                      )}
                    >
                      <Icon
                        size={20}
                        className={cn("shrink-0", active && "text-primary")}
                      />
                      <span className="flex-1 text-sm">{labelFor(s)}</span>
                      {badge > 0 && (
                        <span className="inline-flex items-center justify-center rounded-full bg-primary px-1.5 min-w-[18px] h-[18px] text-[10px] font-semibold text-white tabular-nums">
                          {badge > 9 ? "9+" : badge}
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </>
      )}

      <nav
        aria-label="Cashier navigation"
        className={cn(
          "lg:hidden",
          "fixed bottom-0 inset-x-0 z-40",
          "bg-sidebar text-sidebar-foreground",
          "border-t border-sidebar-border",
          "pb-[env(safe-area-inset-bottom,0px)]",
        )}
      >
        <ul className="flex items-stretch">
          {primary.map((surface) => (
            <NavCell
              key={surface.key}
              to={surface.path!}
              label={labelFor(surface)}
              Icon={surface.icon}
              active={isSurfaceActive(location.pathname, surface.path!)}
              badge={badgeFor(surface)}
            />
          ))}

          {/* "More" — opens the overflow sheet (only if there's overflow). */}
          {overflow.length > 0 && (
            <li className="flex-1">
              <button
                type="button"
                onClick={() => setMoreOpen((v) => !v)}
                aria-expanded={moreOpen}
                aria-haspopup="menu"
                className={cn(
                  "group relative flex w-full flex-col items-center justify-center",
                  "py-2.5 px-1 min-h-[56px] transition-colors duration-150",
                  "outline-none focus-visible:bg-white/5",
                  overflowActive || moreOpen
                    ? "text-white"
                    : "text-sidebar-foreground/70 hover:text-white",
                )}
              >
                <span
                  aria-hidden
                  className={cn(
                    "absolute top-0 h-[3px] w-10 rounded-b-full",
                    overflowActive || moreOpen
                      ? "bg-primary"
                      : "bg-transparent",
                  )}
                />
                <span className="relative">
                  <MoreHorizontal
                    size={20}
                    className={cn(
                      "shrink-0 transition-colors",
                      (overflowActive || moreOpen) && "text-primary",
                    )}
                    aria-hidden
                  />
                  {overflowUnread > 0 && !moreOpen && (
                    <span
                      className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-primary ring-2 ring-sidebar"
                      aria-hidden
                    />
                  )}
                </span>
                <span
                  className={cn(
                    "mt-0.5 text-[10px] font-medium leading-tight",
                    (overflowActive || moreOpen) && "text-white",
                  )}
                >
                  More
                </span>
              </button>
            </li>
          )}
        </ul>
      </nav>
    </>
  );
};

// ─── Single primary tab cell ────────────────────────────────────────

interface NavCellProps {
  to: string;
  label: string;
  Icon: React.ElementType;
  active: boolean;
  badge?: number;
}

const NavCell: React.FC<NavCellProps> = ({
  to,
  label,
  Icon,
  active,
  badge = 0,
}) => (
  <li className="flex-1">
    <Link
      to={to}
      className={cn(
        "group relative flex flex-col items-center justify-center",
        "py-2.5 px-1 min-h-[56px]",
        "transition-colors duration-150",
        "outline-none focus-visible:bg-white/5",
        active ? "text-white" : "text-sidebar-foreground/70 hover:text-white",
      )}
      aria-current={active ? "page" : undefined}
    >
      <span
        aria-hidden
        className={cn(
          "absolute top-0 h-[3px] w-10 rounded-b-full",
          active ? "bg-primary" : "bg-transparent",
        )}
      />
      <span className="relative">
        <Icon
          size={20}
          className={cn("shrink-0 transition-colors", active && "text-primary")}
          aria-hidden
        />
        {badge > 0 && (
          <span
            className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-primary ring-2 ring-sidebar"
            aria-hidden
          />
        )}
      </span>
      <span
        className={cn(
          "mt-0.5 text-[10px] font-medium leading-tight truncate max-w-full px-0.5",
          active && "text-white",
        )}
      >
        {label}
      </span>
    </Link>
  </li>
);

export default CashierBottomNav;
